import { useEffect, useState } from "react";
import { authErrorMessage, forgetPassword, resetPassword, signIn, signUp } from "./auth-client";

const MIN_PASSWORD = 10;

/** Better Auth sends people back to /reset-password?token=… */
function resetTokenFromUrl(): string | null {
  if (window.location.pathname !== "/reset-password") return null;
  return new URLSearchParams(window.location.search).get("token");
}

/**
 * Setting a new password from a reset link.
 *
 * Split out rather than folded into the sign-in form: someone arriving here has
 * one job, and an email field they must not fill in would only invite them to
 * get it wrong.
 */
function SetNewPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }

    setBusy(true);
    try {
      const res = await resetPassword({ newPassword: password, token });
      if (res.error) {
        setError(authErrorMessage(res.error));
        return;
      }
      // Drop the token from the address bar before handing back to sign-in.
      window.history.replaceState({}, "", "/");
      onDone();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-accent-50/60 to-neutral-50 px-6">
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg shadow-neutral-900/5">
        <h1 className="font-[family-name:--font-display] text-xl font-semibold tracking-tight">
          Set a new password
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Then sign in with it. Any other sessions stay signed in until they expire.
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
          <Field label="New password">
            <input
              className={inputClass}
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-neutral-500">At least {MIN_PASSWORD} characters.</p>
          </Field>

          {error && (
            <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-1 rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-accent-500 disabled:opacity-50"
          >
            {busy ? "Working…" : "Set password"}
          </button>
        </form>
      </div>
    </div>
  );
}

interface InvitePreview {
  /** Null for a platform invitation, which belongs to no company. */
  companyName: string | null;
  email: string;
  role: string;
}

/** An invite link is /invite/<token>; the token never goes in the query string. */
function tokenFromPath(): string | null {
  return window.location.pathname.match(/^\/invite\/([^/]+)/)?.[1] ?? null;
}

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const token = tokenFromPath();
  const resetToken = resetTokenFromUrl();
  const [mode, setMode] = useState<"signin" | "signup">(token ? "signup" : "signin");
  const [resetSent, setResetSent] = useState(false);
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const creating = mode === "signup";

  // Resolve the link before asking for a password, so an expired invitation
  // says so up front instead of after someone has filled the form in.
  useEffect(() => {
    if (!token) return;
    let live = true;
    fetch(`/api/invitations/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!live) return;
        if (!res.ok) {
          setInviteError("That invitation has expired or already been used.");
          return;
        }
        const body = (await res.json()) as { invitation: InvitePreview };
        setInvite(body.invitation);
        // The address is fixed by the invitation — the API rejects any other.
        setEmail(body.invitation.email);
      })
      .catch(() => live && setInviteError("Couldn't check that invitation."));
    return () => {
      live = false;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (creating && password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }

    setBusy(true);
    try {
      const res = creating
        ? await signUp.email({
            email,
            password,
            name: name || email,
            // Read by the gate in apps/api/src/index.ts, not by Better Auth.
            ...(token ? { invitationToken: token } : {}),
          } as Parameters<typeof signUp.email>[0])
        : await signIn.email({ email, password });

      if (res.error) {
        // Deliberately not distinguishing "no such account" from "wrong
        // password" — that difference tells an attacker which emails exist.
        setError(creating ? authErrorMessage(res.error) : "That email and password don't match.");
        return;
      }
      // Drop the token from the address bar once it has been spent.
      if (token) window.history.replaceState({}, "", "/");
      onSignedIn();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (resetToken) {
    return <SetNewPassword token={resetToken} onDone={() => window.location.replace("/")} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-accent-50/60 to-neutral-50 px-6">
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg shadow-neutral-900/5">
        <h1 className="font-[family-name:--font-display] text-xl font-semibold tracking-tight">
          Dantria
        </h1>

        {invite ? (
          <p className="mt-1 text-sm text-neutral-500">
            {invite.companyName ? (
              <>
                You've been invited to{" "}
                <span className="text-neutral-900">{invite.companyName}</span> as {invite.role}. Set
                a password to accept.
              </>
            ) : (
              <>
                Set a password to finish setting up your{" "}
                <span className="text-neutral-900">{invite.role}</span> account.
              </>
            )}
          </p>
        ) : (
          <p className="mt-1 text-sm text-neutral-500">
            {creating ? "Create your account." : "Sign in to your account."}
          </p>
        )}

        {inviteError && (
          <p role="alert" className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {inviteError}
          </p>
        )}

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
          {creating && (
            <Field label="Name">
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </Field>
          )}

          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              // Fixed by the invitation: the API rejects a mismatch, so letting
              // it be edited would only produce a confusing rejection.
              readOnly={!!invite}
              disabled={!!invite}
            />
          </Field>

          <Field label="Password">
            <input
              className={inputClass}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={creating ? "new-password" : "current-password"}
            />
            {creating && (
              <p className="mt-1 text-xs text-neutral-500">At least {MIN_PASSWORD} characters.</p>
            )}
          </Field>

          {error && (
            <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || (creating && !!token && !invite)}
            className="mt-1 rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-accent-500 disabled:opacity-50"
          >
            {busy ? "Working…" : creating ? "Create account" : "Sign in"}
          </button>
        </form>

        {!creating &&
          (resetSent ? (
            <p className="mt-4 rounded bg-neutral-100 px-3 py-2 text-sm text-neutral-600">
              If that address has an account, we've emailed a reset link. It's valid for 15 minutes.
            </p>
          ) : (
            <button
              type="button"
              disabled={!email || busy}
              onClick={async () => {
                setError(null);
                setBusy(true);
                try {
                  // Better Auth answers the same way whether or not the address
                  // exists, so this cannot be used to enumerate accounts.
                  await forgetPassword({ email, redirectTo: "/reset-password" });
                  setResetSent(true);
                } catch (err) {
                  setError(authErrorMessage(err));
                } finally {
                  setBusy(false);
                }
              }}
              className="mt-4 block text-sm text-neutral-600 underline underline-offset-4 transition-colors hover:text-accent-600 disabled:opacity-50"
            >
              Forgot your password?
            </button>
          ))}

        {/* Without a token there is nothing to create — sign-up is invitation
            only — so the switch is only offered to someone holding a link. */}
        {token ? (
          <button
            type="button"
            onClick={() => {
              setMode(creating ? "signin" : "signup");
              setError(null);
            }}
            className="mt-4 text-sm text-neutral-600 underline underline-offset-4 transition-colors hover:text-accent-600"
          >
            {creating ? "Already have an account? Sign in" : "Accept the invitation instead"}
          </button>
        ) : (
          <p className="mt-4 text-sm text-neutral-500">
            Accounts are created by invitation. Ask an owner for a link.
          </p>
        )}
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm outline-none transition-colors focus:border-accent-500 focus:ring-2 focus:ring-accent-500/25 disabled:bg-neutral-100 disabled:shadow-none";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}
